import React, { useState, useEffect } from 'react';
import LeaseApiService from '../../services/apiService';
import { FaSave, FaChevronLeft } from 'react-icons/fa';
import { useLocation, useNavigate } from 'react-router-dom';
import { ClipLoader } from 'react-spinners';
import { useTranslation } from 'react-i18next';
import { useCurrency } from '../../../INVENTORY/Layouts/currencyContext';
import PersonalDetails from './details';
import { getCountryFromCurrency } from '../../../INVENTORY/Layouts/currencyHelpers';
import { toast } from 'react-toastify';
import { useSelector } from 'react-redux';
import { getCompany, getName } from '../../../Redux/selecters';
import OtherDetails from './otherDetails';
import Features from './feature';
import ImagesList from './images';
import EscrowAccount from './escrow';
import axios from 'axios';
import { endPoint } from '../../EndPoints/endPoint';
import '../customerMaster/customerForm.css';

// GetPropertyImages returns "url" as an ASP.NET app-relative path
// ("~/Files/PropertyImages/<propertyNo>/") plus a separate "image_Filename" -
// neither is a browsable URL on its own. "~" means the site's own root -
// confirmed to be this exact host (same one apiService.js hardcodes as its
// axios baseURL - REACT_APP_BACKEND_URL points at a different, unrelated host).
const PROPERTY_IMAGE_HOST = 'https://pmkleaseapi.apps.pro360erp.com';
const buildPropertyImageUrl = (item) => {
  const filename = item?.image_Filename || '';
  if (!filename) return '';
  const rawFolder = item?.url || item?.folder || '';
  // Backend can also return "url"/"folder" as an already fully-qualified URL
  // (filename included) instead of the app-relative "~/..." path this was
  // originally written for - prepending the host and re-appending the
  // filename in that case doubled both, e.g.
  // ".../<file>.png/<file>.png" behind a duplicated host.
  if (/^https?:\/\//i.test(rawFolder)) {
    return rawFolder.endsWith(filename) ? rawFolder : `${rawFolder.replace(/\/$/, '')}/${filename}`;
  }
  const folder = rawFolder.replace(/^~/, '');
  const normalizedFolder = folder.endsWith('/') ? folder : `${folder}/`;
  return `${PROPERTY_IMAGE_HOST}${normalizedFolder}${filename}`;
};

// The backend's own "imagePath" field (returned by GetPropertyById/
// GetPropertyImages) can itself already be doubled - both the host and the
// filename segment repeated, e.g.
// "https://HOST/https://HOST/Resources/.../<file>.png/<file>.png" - so this
// has to be cleaned up regardless of whether the url came from imagePath
// directly or was built above from url/folder.
const dedupePropertyImageUrl = (url) => {
  if (!url) return url;
  const protocolMatches = [...url.matchAll(/https?:\/\//g)];
  let result = protocolMatches.length > 1 ? url.slice(protocolMatches[protocolMatches.length - 1].index) : url;
  const segments = result.split('/');
  if (segments.length >= 2 && segments[segments.length - 1] === segments[segments.length - 2]) {
    segments.pop();
    result = segments.join('/');
  }
  return result;
};

const createEmptyRow = () => ({
  id: Date.now() + Math.random(),
  file: null, // for new file (File object)
  fileName: '',
  remarks: '',
  filePath: '',
  isNew: true, // ✅ identify new file
  fullUrl: '', // ✅ for existing file preview
});
const PropertyMasterUpdate = ({ initialData, viewOnly: viewOnlyProp, onClose, onSaved } = {}) => {
  const { t } = useTranslation();
  const apiService = new LeaseApiService();
  let navigate = useNavigate();
  const { currencyPrecision } = useCurrency();
  const userName = useSelector(getName);
  const currencyList = JSON.parse(localStorage.getItem('currencylist')) || [];
  const defaultCountry = currencyPrecision?.code ? getCountryFromCurrency(currencyPrecision?.code, currencyList) : 'bh'; // fallback until API loads
  const [activeFY, setActiveFY] = useState(null);
  const [rowData, setRowData] = useState([]);
  const [activeTab, setActiveTab] = useState(0);
  const [gridVisibility, setGridVisibility] = useState([true, false, false, false, false]);
  const [loading, setloader] = useState(false);
  const [propertyType, setPropertyType] = useState([]);
  const [deletedFiles, setDeletedFiles] = useState([]);
  const [ownerShip, setOwnerShip] = useState([]);
  const [tableLoading, setTableLoading] = useState(false);
  const [countriesList, setCountries] = useState([]);
  const [projectStatus, setProjectStatus] = useState([]);
  const [projectUse, setProjectUse] = useState([]);
  const [community, setCommunity] = useState([]);
  const [cityList, setCityList] = useState([]);
  const [propertyCategory, setPropertyCategory] = useState([]);
  const [attachments, setAttachments] = useState([createEmptyRow()]);
  const location = useLocation();
  const leaseData = initialData ?? location.state;
  const viewOnly = Boolean(viewOnlyProp ?? leaseData?.viewOnly);

  const [transectionData, setTransectionData] = useState({
    propertyNo: '',
    propertyName: '',
    plotNo: '',
    plotSize: '',
    remarks: '',
    propertyDescription: '',
    location: '',
    area: '',
    longitude: '',
    latitude: '',
    video: '',
    community: null,
    country: null,
    city: null,
    propertyType: null,
    ownerShip: null,
    projectStatus: null,
    category: null,
    propertyUse: null,
  });

  useEffect(() => {
    fetchDropdownData();
    const financialYears = JSON.parse(localStorage.getItem('financialYear')) || [];
    const activeFinancialYear = financialYears?.find((fy) => fy.isActive);
    if (activeFinancialYear) {
      setActiveFY({ label: activeFinancialYear.name, value: activeFinancialYear });
    }
  }, []);
  const fetchDropdownData = async () => {
    try {
      const params = new URLSearchParams();
      params.append('dropdownTypes', 'PropertyType,ProjectStatus,Country,Community,Ownership,PropertyStatus,PropertyCategory');
      const res = await apiService.getApiParamater('PROPERTY_MULTIDROP', `?${params.toString()}`);
      setPropertyType(res?.data?.dropdowns?.PropertyType);
      setCommunity(res?.data?.dropdowns?.Community);
      setProjectStatus(res?.data?.dropdowns?.ProjectStatus);
      setOwnerShip(res?.data?.dropdowns?.Ownership);
      setCountries(res?.data?.dropdowns?.Country);
      setPropertyCategory(res?.data?.dropdowns?.PropertyCategory);
      if (leaseData) {
        const unitres = await apiService.getApiParamater('GET_PROPERTY_BY_ID', `/${leaseData?.property_No}`);
        let unitData = unitres?.data?.result?.property;
        const community = res?.data?.dropdowns?.Community?.find((dep) => dep.text === unitData?.community_Name);
        const country = res?.data?.dropdowns?.Country?.find((dep) => dep.text === unitData?.country);
        const propertyType = res?.data?.dropdowns?.PropertyType?.find((dep) => dep.text === unitData?.property_Type);
        const projectStatus = res?.data?.dropdowns?.ProjectStatus?.find((dep) => dep.text === unitData?.project_Status);
        const ownerShip = res?.data?.dropdowns?.Ownership?.find((dep) => dep.text === unitData?.property_Class);
        const category = res?.data?.dropdowns?.PropertyCategory?.find((dep) => dep.text === unitData?.property_Category);
        const cityParams = new URLSearchParams();
        cityParams.append('DropdownFor', 'CitiesByCountry');
        cityParams.append('CascadValue', country?.text);
        const cityres = await apiService.getApiParamater('PROPERTY_CASCAD', `?${cityParams.toString()}`);
        setCityList(cityres?.data?.items || []);
        const city = cityres?.data?.items?.find((dep) => dep.text === unitData?.city);
        setTransectionData({
          propertyNo: unitData?.property_No || '',
          propertyName: unitData?.description || '',
          plotNo: unitData?.plot_No || '',
          plotSize: unitData?.plot_Size || '',
          remarks: unitData?.remarks || '',
          propertyDescription: unitData?.descriptionBI || '',
          location: unitData?.location || '',
          area: unitData?.gross_Area || '',
          longitude: unitData?.longitude || '',
          latitude: unitData?.latitude || '',
          video: unitData?.exp7 || '',
          community: community ? { label: community?.text, value: community } : null,
          country: country ? { label: country?.text, value: country } : null,
          propertyType: propertyType ? { label: propertyType?.text, value: propertyType } : null,
          projectStatus: projectStatus ? { label: projectStatus?.text, value: projectStatus } : null,
          ownerShip: ownerShip ? { label: ownerShip?.text, value: ownerShip } : null,
          city: city ? { label: city?.text, value: city } : null,
          category: category ? { label: category?.text, value: category } : null,
        });
        // Images are already part of this same GET_PROPERTY_BY_ID response
        // (result.images) - no separate GET_PROPERTY_IMAGE call needed. Each
        // row's own "imagePath" is already a complete, ready-to-use URL
        // (confirmed live) - "url"/"folder" are only the app-relative
        // fallback for older rows that predate imagePath being populated.
        // Every property has placeholder rows with image_Filename "0" left
        // over from before the real upload was wired up - filter those out.
        const imageRows = (unitres?.data?.result?.images || []).filter(
          (item) => item?.image_Filename && item.image_Filename !== '0',
        );
        if (imageRows.length) {
          const apiFiles = imageRows.map((item) => ({
            id: Date.now() + Math.random(),
            file: null,
            fileName: item?.image_Filename || '',
            remarks: item?.image_Description || '',
            filePath: item?.folder || item?.url || '',
            recordId: item?.recordId ?? item?.RecordId ?? '',
            isNew: false, // ✅ OLD FILE
            fullUrl: dedupePropertyImageUrl(item?.imagePath) || buildPropertyImageUrl(item), // ✅ for preview
          }));
          setAttachments([...apiFiles, createEmptyRow()]);
        }
      }
    } catch (error) {
      console.error('Error fetching dropdown data:', error);
    }
  };
  const handleTabClick = (tabIndex) => {
    setActiveTab(tabIndex);
    const newGridVisibility = gridVisibility.map((_, index) => index === tabIndex);
    setGridVisibility(newGridVisibility);
  };
  const handleBackClick = () => {
    if (onClose) {
      onClose();
      return;
    }
    window.history.back();
  };
  const handleSave = async () => {
    if (viewOnly) {
      return;
    }
    // Backend's AddPropertyImage requires ImageDescription - it's sourced
    // from this row's own Remarks input, so a blank Remarks would otherwise
    // only surface as a 400 from that endpoint after the property itself
    // already saved. Catch it up front instead.
    const pendingImages = attachments.filter((item) => item.file instanceof File && item.isNew);
    if (pendingImages.some((item) => !item.remarks?.trim())) {
      toast.warning('Please enter a description (Remarks) for each image before saving.');
      return;
    }
    try {
      setloader(true);
      const currentDatetimeUTC = new Date().toISOString();
      const today = new Date();
      let property = {
        property_No: transectionData?.propertyNo || '0',
        description: transectionData?.propertyName || '0',
        descriptionBI: transectionData?.propertyDescription || '0',
        project: '0',
        location: transectionData?.location || '0',
        address1: '0',
        city: transectionData?.city?.label || '0',
        country: transectionData?.country?.label || '0',
        subDistrict: '0',
        district: '0',
        plot_No: transectionData?.plotNo || '0',
        subPlot_No: '0',
        plot_Size: transectionData?.plotSize || '0',
        community_No: transectionData?.community?.value?.value || '0',
        community_Name: transectionData?.community?.label || '0',
        subCommunity_No: '0',
        subCommunity_Name: '0',
        contact_Person: '0',
        contact_No: '0',
        rate_Sqft: '0',
        developer: '0',
        dContactNo: '0',
        dEmail: '0',
        dContactPerson: '0',
        subDeveloper: '0',
        sdContactNo: '0',
        sdEmail: '0',
        sdContactPerson: '0',
        agent: '0',
        subAgent: '0',
        contractor: '0',
        subContractor: '0',
        total_Units: '0',
        total_Parking: '0',
        floors: '0',
        built_Area: '0',
        gross_Area: transectionData?.area || '0',
        property_Type: transectionData?.propertyType?.label || '0',
        property_Category: transectionData?.category?.label || '0',
        property_Class: transectionData?.ownerShip?.label || '0',
        start_Date: '2026-05-04T17:22:52.606Z',
        end_Date: '2026-05-04T17:22:52.606Z',
        project_Cost: '0',
        project_Status: transectionData?.projectStatus?.label || '0',
        facilities: '0',
        remarks: transectionData?.remarks || '0',
        escrow_Bank: '0',
        escrow_BankAcc: '0',
        latitude: transectionData?.latitude || '0',
        longitude: transectionData?.longitude || '0',
        image_Filename: '0',
        ownerNo: '0',
        ownerName: '0',
        ownerContactNo: '0',
        ownerEmail: '0',
        referredBy: '0',
        referredOn: '2026-05-04T17:22:52.606Z',
        exp1: '0',
        exp2: '0',
        exp3: '0',
        exp4: '0',
        exp5: '0',
        exp6: '0',
        cardNumber: '0',
        bondNumber: '0',
        bondType: '0',
        bondDate: '2026-05-04T17:22:52.606Z',
        exp7: transectionData?.video || '0',
        exp8: '0',
        exp9: '0',
        exp10: '0',
        exp11: '0',
        exp12: '0',
        modifiedBy: '0',
        modifiedOn: today,
        createdBy: '0',
        createdOn: today,
      };
      // Real images are persisted separately via AddPropertyImage (see
      // handleUpload below) - a placeholder entry here used to create a
      // leftover row with image_Filename "0" on every save, which is exactly
      // what fetchDropdownData above has to filter back out on load.
      const body = {
        propertyMain: {
          property: property,
          images: [],
          isEdit: true,
        },
      };

      // Property No is already known for an existing property (unlike create,
      // where the backend generates it), so files + AddPropertyImage can run
      // before UPDATE_PROPERTY instead of after. Running upload first is what
      // fixes UPDATE_PROPERTY not persisting the image correctly / the
      // "file not found" error it was producing when the upload followed it.
      if (attachments) {
        try {
          await handleUpload(transectionData?.propertyNo || leaseData?.property_No);
        } catch (uploadError) {
          console.error('Error uploading property images', uploadError);
          toast.warning(uploadError?.message || 'Image upload failed. Please try uploading the image again.');
        }
      }

      const postRes = await apiService.put('UPDATE_PROPERTY', body);
      if (postRes?.data?.statusCode === 200) {
        toast.success(postRes?.data?.message);
        setloader(false);
        if (onSaved) {
          onSaved();
        } else {
          navigate('/Customer/Property/Master/Property/Listing');
        }
      } else {
        toast.warning(`${postRes?.data?.message}`);
        setloader(false);
      }
    } catch (error) {
      console.error('Error Save Data', error);
      setloader(false);
    } finally {
      setloader(false);
    }
  };

  const handleUpload = async (ref_No) => {
    const formData = new FormData();

    let token = localStorage.getItem('Token');
    let db = localStorage.getItem('DataBase');
    const validFiles = attachments.filter((item) => item.file instanceof File && item.isNew);
    if (!validFiles.length) return;

    validFiles.forEach(({ file }) => {
      formData.append('files', file);
    });

    formData.append('refNo', ref_No);
    formData.append('fileCategory', 'Images');

    const response = await axios.post(endPoint.UNIT_FILE, formData, {
      headers: {
        accept: 'text/plain',
        apikey: 'V+kBloEwqqVho1PINmQI0VJQwSBX2l/e',
        Authorization: `Bearer ${token}`,
        db: db,
      },
    });
    // Uploading the raw file bytes to the generic FileUpload endpoint above is
    // only half the job (same two-step pattern already proven working in
    // unitMaster/createUnit.js's handleUpload/additionalDataUpload) - it never
    // actually attaches the image to the property record. That second step
    // was missing entirely here.
    const results = response?.data?.result || [];
    if (!response?.data?.isSuccessful && !results.length) {
      throw new Error(response?.data?.message || 'Image upload failed');
    }
    // AddPropertyImage's real DTO only requires PropertyNo/ImageFileName/
    // ImageDescription (confirmed from its 400 validation response) - the
    // fields guessed earlier (ref_No, image_Description, folder, url, exp1-3,
    // etc.) don't exist on it at all. ImageDescription comes from this row's
    // own Remarks input (validated non-empty in handleSave above), paired
    // back to its upload result by position, same convention used for this
    // same UNIT_FILE endpoint in unitMaster/createUnit.js and
    // customerDocumentHelpers.js's uploadCustomerDocuments.
    await Promise.all(
      results.map((result, index) =>
        apiService.post('UPLOAD_PROPERTY_IMAGE', {
          PropertyNo: ref_No,
          ImageFileName: result?.fileName || '',
          ImageDescription: validFiles[index]?.remarks || '',
        }),
      ),
    );
  };
  return (
    <div className="d-flex flex-column property-modal-form cf-modal" style={{ height: '85vh' }}>
      <div className="cf-head d-flex align-items-center justify-content-between flex-shrink-0">
        <div>
          <h5 className="mb-0">{viewOnly ? t('propertyMaster.viewFormTitle') : t('propertyMaster.editFormTitle')}</h5>
          {viewOnly && <span className="text-muted small">{t('common.viewOnlyNotice')}</span>}
        </div>
        <button type="button" className="btn-close" aria-label="Close" onClick={handleBackClick}></button>
      </div>
      <div className="cf-body" style={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
      <div className="cf-tabs-wrap">
        <div className="nav nav-pills overslid d-flex property-modal-tabs cf-tabs">
          <div className={`dashitems sidebar-text center ${activeTab === 0 ? 'sideActive' : ''}`} onClick={() => handleTabClick(0)}>
            {t('propertyMaster.tabs.details')}
          </div>
          <div className={`dashitems sidebar-text center ${activeTab === 3 ? 'sideActive' : ''}`} onClick={() => handleTabClick(3)}>
            {t('propertyMaster.tabs.images')}
          </div>
        </div>
      </div>
      {gridVisibility[0] && (
        <PersonalDetails
          readOnly={viewOnly}
          tableLoading={tableLoading}
          propertyType={propertyType}
          ownerShip={ownerShip}
          setTransectionData={setTransectionData}
          rowData={rowData}
          projectUse={projectUse}
          community={community}
          propertyCategory={propertyCategory}
          transectionData={transectionData}
          projectStatus={projectStatus}
          cityList={cityList}
          countriesList={countriesList}
          setCityList={setCityList}
        />
      )}
      {gridVisibility[3] && (
        <ImagesList
          readOnly={viewOnly}
          attachments={attachments}
          deletedFiles={deletedFiles}
          setDeletedFiles={setDeletedFiles}
          setAttachments={setAttachments}
          createEmptyRow={createEmptyRow}
        />
      )}
      </div>
      <div className="cf-foot d-flex justify-content-end gap-3 flex-shrink-0">
        {!viewOnly &&
          (loading === true ? (
            <div className="mainbtn flex-end gap-2">
              <ClipLoader color="#ffffff" className="mt-1" size={20} />
            </div>
          ) : (
            <div className="mainbtn flex-end gap-2" onClick={handleSave}>
              <FaSave color="#fff" className="center mt-1 mx-2" />
              <div className="center">{t('common.save')}</div>
            </div>
          ))}
        <div className={viewOnly ? 'mainbtn flex-end gap-2' : 'mainbtnRed flex-end gap-2'} onClick={handleBackClick}>
          <FaChevronLeft color="#fff" className="center mt-1 mx-2" />
          <div className="center">{viewOnly ? t('common.close') : t('common.cancel')}</div>
        </div>
      </div>
    </div>
  );
};
export default PropertyMasterUpdate;
